import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  TrendingUp, TrendingDown, Activity, Thermometer, Droplets, 
  Sprout, AlertCircle, BarChart3, PieChart, Calendar
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AnalyticsSummary {
  totalDevices: number;
  activeDevices: number;
  offlineDevices: number;
  totalSensors: number;
  alertsCount: number;
  avgTemperature: number;
  avgHumidity: number;
  avgSoilMoisture: number;
  dataPoints: number;
  uptime: number;
}

interface TrendData {
  date: string;
  temperature: number;
  humidity: number;
  soilMoisture: number;
  alerts: number;
  activeDevices: number;
}

interface Distribution {
  soilMoisture: { optimal: number; warning: number; critical: number };
  temperature: { optimal: number; warning: number; critical: number };
  humidity: { optimal: number; warning: number; critical: number };
}

const DemoAnalytics: React.FC = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<7 | 14 | 30>(7);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');

  const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/demo`;

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0) {
      fetchAnalyticsData();
    }
  }, [selectedPeriod, selectedLocation, locations]);

  const fetchLocations = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Augmenté à 1s
      const response = await fetch(`${apiUrl}/locations`);
      if (response.ok) {
        const data = await response.json();
        setLocations(Array.isArray(data) ? data : []);
      } else if (response.status === 429) {
        console.warn('Rate limited on locations, retrying...');
        setTimeout(fetchLocations, 5000);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Add longer delays between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1500)); // Augmenté à 1.5s
      
      const [summaryRes, trendsRes, distributionRes] = await Promise.all([
        fetch(`${apiUrl}/analytics/summary`),
        fetch(`${apiUrl}/analytics/trends?days=${selectedPeriod}`),
        fetch(`${apiUrl}/analytics/distribution`)
      ]);

      if (summaryRes.ok && trendsRes.ok && distributionRes.ok) {
        const summaryData = await summaryRes.json();
        const trendsData = await trendsRes.json();
        const distributionData = await distributionRes.json();

        setSummary(summaryData);
        setTrends(trendsData);
        setDistribution(distributionData);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-xl shadow-2xl p-6 sm:p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3">
              <BarChart3 className="h-10 w-10" />
              <span>Analyses et Statistiques</span>
            </h1>
            <p className="text-blue-50 text-lg">
              Vue d'ensemble des performances et tendances
            </p>
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="flex gap-3">
            {[7, 14, 30].map(days => (
              <button
                key={days}
                onClick={() => setSelectedPeriod(days as 7 | 14 | 30)}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all ${
                  selectedPeriod === days
                    ? 'bg-white text-indigo-600 shadow-lg'
                    : 'bg-indigo-500/30 text-white hover:bg-indigo-500/50'
                }`}
              >
                {days} jours
              </button>
            ))}
          </div>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="px-4 py-2.5 rounded-lg font-semibold bg-white text-indigo-600 shadow-lg"
          >
            <option value="all">Toutes les localisations</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/40 dark:to-emerald-800/40 rounded-xl shadow-lg p-5 border border-emerald-200 dark:border-emerald-700">
            <div className="flex items-center justify-between mb-3">
              <Activity className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              <div className="text-right">
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-200">Appareils</p>
                <p className="text-3xl font-black text-emerald-600 dark:text-emerald-300">
                  {summary.activeDevices}/{summary.totalDevices}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-200">
              <TrendingUp className="h-4 w-4" />
              <span>{((summary.activeDevices / summary.totalDevices) * 100).toFixed(1)}% actifs</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 rounded-xl shadow-lg p-5 border border-blue-200 dark:border-blue-700">
            <div className="flex items-center justify-between mb-3">
              <Thermometer className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div className="text-right">
                <p className="text-sm font-semibold text-blue-700 dark:text-blue-200">Temp. Moy</p>
                <p className="text-3xl font-black text-blue-600 dark:text-blue-300">
                  {summary.avgTemperature.toFixed(1)}°C
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-200">
              <Activity className="h-4 w-4" />
              <span>Conditions optimales</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/40 dark:to-cyan-800/40 rounded-xl shadow-lg p-5 border border-cyan-200 dark:border-cyan-700">
            <div className="flex items-center justify-between mb-3">
              <Droplets className="h-8 w-8 text-cyan-600 dark:text-cyan-400" />
              <div className="text-right">
                <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-200">Humidité</p>
                <p className="text-3xl font-black text-cyan-600 dark:text-cyan-300">
                  {summary.avgHumidity.toFixed(1)}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-cyan-700 dark:text-cyan-200">
              <TrendingUp className="h-4 w-4" />
              <span>Niveau bon</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/40 dark:to-amber-800/40 rounded-xl shadow-lg p-5 border border-amber-200 dark:border-amber-700">
            <div className="flex items-center justify-between mb-3">
              <Sprout className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              <div className="text-right">
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-200">Sol</p>
                <p className="text-3xl font-black text-amber-600 dark:text-amber-300">
                  {summary.avgSoilMoisture.toFixed(1)}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-200">
              <Activity className="h-4 w-4" />
              <span>Humidité idéale</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/40 dark:to-purple-800/40 rounded-xl shadow-lg p-5 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center justify-between mb-3">
              <AlertCircle className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              <div className="text-right">
                <p className="text-sm font-semibold text-purple-700 dark:text-purple-200">Alertes</p>
                <p className="text-3xl font-black text-purple-600 dark:text-purple-300">
                  {summary.alertsCount}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-200">
              <Activity className="h-4 w-4" />
              <span>À traiter</span>
            </div>
          </div>
        </div>
      )}

      {/* Trends Chart Simulation */}
      {trends.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            <span>Évolution des Mesures ({selectedPeriod} derniers jours)</span>
          </h2>
          <div className="space-y-6">
            {/* Simplified bar chart visualization */}
            {trends.slice(0, 7).map((trend, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {format(new Date(trend.date), 'dd MMM', { locale: fr })}
                  </span>
                  <div className="flex gap-4 text-xs">
                    <span className="text-blue-600 dark:text-blue-400">🌡️ {trend.temperature.toFixed(1)}°C</span>
                    <span className="text-cyan-600 dark:text-cyan-400">💧 {trend.humidity.toFixed(1)}%</span>
                    <span className="text-amber-600 dark:text-amber-400">🌱 {trend.soilMoisture.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div
                    className="h-8 bg-gradient-to-r from-blue-400 to-blue-600 rounded transition-all"
                    style={{ width: `${(trend.temperature / 40) * 100}%` }}
                  ></div>
                  <div
                    className="h-8 bg-gradient-to-r from-cyan-400 to-cyan-600 rounded transition-all"
                    style={{ width: `${(trend.humidity / 100) * 100}%` }}
                  ></div>
                  <div
                    className="h-8 bg-gradient-to-r from-amber-400 to-amber-600 rounded transition-all"
                    style={{ width: `${(trend.soilMoisture / 100) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Distribution */}
      {distribution && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(distribution).map(([key, values]) => {
            const total = values.optimal + values.warning + values.critical;
            const labels: Record<string, string> = {
              soilMoisture: '🌱 Humidité Sol',
              temperature: '🌡️ Température',
              humidity: '💧 Humidité Air'
            };

            return (
              <div key={key} className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  <span>{labels[key]}</span>
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">✅ Optimal</span>
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                      {values.optimal} ({((values.optimal / total) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-400 to-green-600"
                      style={{ width: `${(values.optimal / total) * 100}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">⚠️ Attention</span>
                    <span className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                      {values.warning} ({((values.warning / total) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600"
                      style={{ width: `${(values.warning / total) * 100}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">🚨 Critique</span>
                    <span className="text-lg font-bold text-red-600 dark:text-red-400">
                      {values.critical} ({((values.critical / total) * 100).toFixed(0)}%)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-400 to-red-600"
                      style={{ width: `${(values.critical / total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* System Stats */}
      {summary && (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Activity className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            <span>Statistiques Système</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-4xl font-black text-emerald-600 dark:text-emerald-400 mb-2">
                {summary.uptime}%
              </p>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Disponibilité</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-blue-600 dark:text-blue-400 mb-2">
                {summary.totalSensors}
              </p>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Capteurs totaux</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-purple-600 dark:text-purple-400 mb-2">
                {summary.dataPoints.toLocaleString()}
              </p>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Points de données</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-amber-600 dark:text-amber-400 mb-2">
                3
              </p>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Exploitations</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoAnalytics;
